import {
  DataFlowEdgeForValidation,
  ProcessFlowchartForValidation,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  ensureDataFlowEdgeSourceIsValid,
  getSourceAndTargetFieldsForDataFlowEdge,
} from 'processes/graph-builder-processes/validation/validateProcessFlowchartRules';
import buildValidationFlowchartFromStaticData from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/buildValidationFlowchartFromStaticData';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {StaticFlowchartNodeData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {
  findNodeIdsByPTDVClientId,
  FlowchartEdgeChangeType,
  FlowchartNodeChangeType,
  TransitionChange,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {getTransitionSourceByNodeIdPairKey} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import {Transition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionTypes';
import {
  FlowchartChangeGenerator,
  FlowchartChanges,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';

/**
 * Collects the IDs of data flow edges that are affected by node schema updates.
 * Returns edges that are connected to (incoming or outgoing from) the updated nodes.
 */
export function collectAffectedDataFlowEdgeIdsForNodeUpdates(
  flowchartChanges: FlowchartChanges,
  edges: Array<FlowchartStaticEdgeData>
): Array<string> {
  const affectedEdgeIds = new Set<string>();
  const updatedNodeIds = new Set<string>();

  for (const change of flowchartChanges.nodeChanges) {
    if (change.type === FlowchartNodeChangeType.REPLACE) {
      updatedNodeIds.add(change.id);
    }
  }

  for (const edge of edges) {
    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      if (updatedNodeIds.has(edge.source) || updatedNodeIds.has(edge.target)) {
        affectedEdgeIds.add(edge.id);
      }
    }
  }

  return Array.from(affectedEdgeIds);
}

/**
 * @deprecated Use collectAffectedDataFlowEdgeIdsForTransitionChanges instead.
 * Collects the IDs of data flow edges that are affected by transition source changes.
 * Returns edges between the specific node pairs that had transition source changes.
 */
export function collectAffectedDataFlowEdgeIdsForTransitionSourceChanges(
  flowchartChanges: FlowchartChanges,
  edges: Array<FlowchartStaticEdgeData>
): Array<string> {
  const affectedEdgeIds = new Set<string>();
  const transitionSourceNodePairs = new Set<string>();

  for (const change of flowchartChanges.transitionSourceChanges) {
    transitionSourceNodePairs.add(change.id);
  }

  for (const edge of edges) {
    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      const edgeNodePairKey = getTransitionSourceByNodeIdPairKey(edge.source, edge.target);
      if (transitionSourceNodePairs.has(edgeNodePairKey)) {
        affectedEdgeIds.add(edge.id);
      }
    }
  }

  return Array.from(affectedEdgeIds);
}

/**
 * Collects the IDs of data flow edges that are affected by transition changes.
 * Returns edges between nodes whose PTDVs match the transitions that had source changes.
 */
export function collectAffectedDataFlowEdgeIdsForTransitionChanges(
  transitionChanges: Array<TransitionChange>,
  nodes: Array<StaticFlowchartNodeData>,
  edges: Array<FlowchartStaticEdgeData>,
  transitions: Record<string, Transition>
): Array<string> {
  const affectedEdgeIds = new Set<string>();

  for (const change of transitionChanges) {
    if (change.type !== 'update' || change.changes.transitionSource == null) {
      continue;
    }

    const transition = transitions[change.id];
    if (transition == null) {
      continue;
    }

    const sourcePTDVId = change.changes.transitionSource.sourcePTDVId;
    const targetPTDVId = transition.targetPTDVId;

    const sourceNodeIds = findNodeIdsByPTDVClientId(nodes, sourcePTDVId);
    const targetNodeIds = findNodeIdsByPTDVClientId(nodes, targetPTDVId);

    for (const edge of edges) {
      if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        if (sourceNodeIds.includes(edge.source) && targetNodeIds.includes(edge.target)) {
          affectedEdgeIds.add(edge.id);
        }
      }
    }
  }

  return Array.from(affectedEdgeIds);
}

export function doesDataFlowEdgeHaveArchivedOrDeletedFields(
  edge: DataFlowEdgeForValidation,
  flowchart: ProcessFlowchartForValidation
): boolean {
  const sourceAndTargetFields = getSourceAndTargetFieldsForDataFlowEdge(edge, flowchart);
  if (sourceAndTargetFields == null) {
    return true;
  }
  if (
    (sourceAndTargetFields.sourceField.archiveData != null &&
      sourceAndTargetFields.sourceField.archiveData.archived) ||
    (sourceAndTargetFields.targetField.archiveData != null &&
      sourceAndTargetFields.targetField.archiveData.archived)
  ) {
    return true;
  }
  return false;
}

export function healDataFlowEdges(changes: FlowchartChanges): FlowchartChangeGenerator {
  return (currentFlowchart: FlowchartConfigWithMetadata) => {
    // Check for data flow validation errors
    const validationFlowchart = buildValidationFlowchartFromStaticData({
      id: currentFlowchart.metadata.currentVersionId ?? '',
      staticNodesData: currentFlowchart.staticFlowchart.nodesStaticData,
      staticEdgesData: currentFlowchart.staticFlowchart.edgesStaticData,
      options: currentFlowchart.metadata,
    });
    const affectedDataFlowEdgeIds = [
      ...collectAffectedDataFlowEdgeIdsForNodeUpdates(
        changes,
        currentFlowchart.staticFlowchart.edgesStaticData
      ),
      ...collectAffectedDataFlowEdgeIdsForTransitionChanges(
        changes.transitionChanges,
        currentFlowchart.staticFlowchart.nodesStaticData,
        currentFlowchart.staticFlowchart.edgesStaticData,
        currentFlowchart.metadata.transitions
      ),
    ];
    const edgesToRemove: Array<string> = [];
    // Check affected data flow edges for validation errors (invalid source types)
    for (const edge of validationFlowchart.flowchart.edges) {
      if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW && affectedDataFlowEdgeIds.includes(edge.id)) {
        const validationErrors =
          ensureDataFlowEdgeSourceIsValid({
            edge,
            flowchart: validationFlowchart.flowchart,
            usingFlowchartFirstConfig: false,
          }) ?? [];
        if (validationErrors.length > 0) {
          edgesToRemove.push(edge.id);
        }
        // Remove data flow edges with archived source or target fields
        if (doesDataFlowEdgeHaveArchivedOrDeletedFields(edge, validationFlowchart.flowchart)) {
          edgesToRemove.push(edge.id);
        }
      }
    }

    // If there are validation errors, remove the data flow edges that are causing the errors
    return {
      edgeChanges: edgesToRemove.map((edgeId) => ({
        type: FlowchartEdgeChangeType.REMOVE,
        id: edgeId,
      })),
    };
  };
}
